import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ItensService, {
  Item,
  ItemCreate,
  ItemUpdate,
  ItemFilter,
} from '../../services/itensService';
import CategoriasService, { Categoria } from '../../services/categoriasService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const ItensPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [itens, setItens] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [categorias, setCategorias] = useState<EntidadeSimples[]>([]);
  const [itensPrincipais, setItensPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<ItemFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState<Item | null>(null);
  const [newItem, setNewItem] = useState<ItemCreate>({ nome: '' });
  const [editItem, setEditItem] = useState<ItemUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar categorias para dropdown
      const categoriasData = await CategoriasService.getAllCategorias();
      setCategorias(categoriasData);
      
      // Buscar itens para uso como "item principal"
      const itensData = await ItensService.getAllItens();
      setItensPrincipais(itensData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchItens = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await ItensService.getItens(filter, skip, limit);
      setItens(data);
      
      // Obter contagem total para paginação
      const count = await ItensService.getItensCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar itens:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar itens',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar itens ao iniciar a página ou mudar filtros/paginação
    fetchItens();
    // Carregar referências
    carregarReferencias();
  }, [fetchItens, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof ItemFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateItem = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newItem.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do item é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await ItensService.createItem(newItem);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Item criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewItem({ nome: '' });
      
      // Recarregar dados
      fetchItens();
      
      // Atualizar itens principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar item:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar item',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (item: Item) => {
    setCurrentItem(item);
    // Inicializar dados para edição
    setEditItem({
      nome: item.nome,
      categoria_id: item.categoria_id,
      principal_id: item.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditItem = async () => {
    if (!currentItem) return;

    try {
      // Verificar campos obrigatórios
      if (!editItem.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do item é obrigatório',
          severity: 'error',
        });
        return;
      }

      await ItensService.updateItem(currentItem.id, editItem);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Item atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentItem(null);
      setEditItem({});
      
      // Recarregar dados
      fetchItens();
      
      // Atualizar itens principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar item:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar item',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (item: Item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleDeleteItem = async () => {
    if (!currentItem) return;

    try {
      await ItensService.deleteItem(currentItem.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Item excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentItem(null);
      
      // Recarregar dados
      fetchItens();
      
      // Atualizar itens principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir item',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <InventoryIcon sx={{ mr: 2 }} />
          Gerenciamento de Itens
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="categoria-filter-label">Categoria</InputLabel>
                <Select
                  labelId="categoria-filter-label"
                  label="Categoria"
                  name="categoria_id"
                  value={filter.categoria_id || ''}
                  onChange={(e) => handleFilterChange('categoria_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categorias.map((categoria) => (
                    <MenuItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Novo Item
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Itens */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Item Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : itens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum item encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  itens.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.nome}</TableCell>
                      <TableCell>
                        {categorias.find(c => c.id === item.categoria_id)?.nome || '-'}
                      </TableCell>
                      <TableCell>
                        {itensPrincipais.find(i => i.id === item.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(item)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newItem.nome || ''}
                onChange={(e) => setNewItem({ ...newItem, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-categoria-label">Categoria</InputLabel>
                <Select
                  labelId="new-categoria-label"
                  label="Categoria"
                  name="categoria_id"
                  value={newItem.categoria_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewItem)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categorias.map((categoria) => (
                    <MenuItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-item-principal-label">Item Principal</InputLabel>
                <Select
                  labelId="new-item-principal-label"
                  label="Item Principal"
                  name="principal_id"
                  value={newItem.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewItem)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {itensPrincipais.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateItem}
            disabled={!newItem.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editItem.nome || ''}
                onChange={(e) => setEditItem({ ...editItem, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-categoria-label">Categoria</InputLabel>
                <Select
                  labelId="edit-categoria-label"
                  label="Categoria"
                  name="categoria_id"
                  value={editItem.categoria_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditItem)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categorias.map((categoria) => (
                    <MenuItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-item-principal-label">Item Principal</InputLabel>
                <Select
                  labelId="edit-item-principal-label"
                  label="Item Principal"
                  name="principal_id"
                  value={editItem.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditItem)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {itensPrincipais
                    .filter(item => currentItem ? item.id !== currentItem.id : true)
                    .map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditItem}
            disabled={!editItem.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o item "{currentItem?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteItem}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default ItensPage;
